// ui/Select.jsx
import React, { useMemo, forwardRef } from "react";
import ReactSelect from "react-select";
import { _t } from '../../utils/i18n';
import { useSelectStyles } from '../../utils/selectStyles';

const ALL = "ALL";
const isAll = (opt) => !!opt && opt.value === ALL;

const Select = forwardRef(function Select(props, ref) {
  const {
    isMulti,
    enableSelectAll = true,
    disableSort = false,
    value,
    onChange,
    ...rest
  } = props;

  let options = props.options || [];

  // filter out options with no label
  options = options.filter((o) => o.label);

  if (!disableSort && options.length > 0) {
    options = options.sort((a, b) => a.label.localeCompare(b.label));
  }

  // Only activate select all if multi and there are more than 1 option
  const active = isMulti && enableSelectAll && options.length > 1;
  const selectStyles = useSelectStyles();

  const computedOptions = useMemo(() => {
    if (!active) return options;
    const allSelected = value && value.length === options.length && options.length > 0;
    if (allSelected) return options;
    return [{ value: ALL, label: _t('Select All') }, ...options];
  }, [active, options, value]);

  const computedValue = useMemo(() => {
    if (!active) return value;
    const current = (value || []).filter((opt) => !isAll(opt));
    if (current.length === options.length && options.length > 0) {
      return options;
    }
    return current;
  }, [active, value, options]);

  const handleChange = (selected, comp) => {
    if (!active) {
      onChange?.(selected, comp);
      return;
    }

    if (comp?.action === "select-option" && isAll(comp.option)) {
      onChange?.(options, comp);
      return;
    }
    if (comp?.action === "deselect-option" && isAll(comp.option)) {
      onChange?.([], comp);
      return;
    }

    const cleaned = (selected || []).filter((o) => !isAll(o));
    onChange?.(
      cleaned.length === options.length ? options : cleaned,
      comp
    );
  };

  return (
    <ReactSelect
      {...rest}
      ref={ref}
      isMulti={isMulti}
      options={computedOptions}
      value={computedValue}
      onChange={handleChange}
      styles={selectStyles}
    />
  );
});

export default Select;
